<template>
  <div class="col-md-6 mb-2">
    <div class="device-item-card card-item">
      <!-- 1줄: 장치명 + 컨트롤 -->
      <div class="device-item-top">
        <span class="device-item-name">{{ props.group?.name ? props.group.name + " · " : "" }}{{ props.device?.name || "" }}</span>
        <!-- 흡수된 물리 호스트: 씬 멤버 고정(해제 불가) -->
        <span
          v-if="includeLocked && (isMappingSelectMode || isOptionalLampInclude)"
          class="btn btn-sm btn-primary include-locked-badge"
        >기본</span>
        <!-- 매핑 선택: 포함일 때만 상태 표시를 바꿀 수 있다 -->
        <div v-else-if="isMappingSelectMode" class="device-item-switches">
          <OnOffSwitch
            :value="showStateOn"
            :disabled="!isInclude"
            text-on="상태 표시"
            text-off="상태 미표시"
            @onValueChange="onShowStateClicked"
          />
          <OnOffSwitch
            :value="powerValue"
            text-on="포함"
            text-off="미포함"
            @onValueChange="onPowerClicked"
          />
        </div>
        <OnOffSwitch
          v-else-if="isOptionalLampInclude"
          :value="powerValue"
          text-on="포함"
          text-off="미포함"
          @onValueChange="onPowerClicked"
        />
        <OnOffSwitch
          v-else-if="isLampType"
          :value="powerValue"
          @onValueChange="onPowerClicked"
        />
        <!-- 가상버튼 커튼 멤버 선택: 포함/미포함 -->
        <OnOffSwitch
          v-else-if="isCurtainType"
          :value="isInclude"
          text-on="포함"
          text-off="미포함"
          @onValueChange="onCurtainSelectToggled"
        />
      </div>

      <div class="device-item-body">
        <!-- 매핑 선택 화면에서는 밝기/색온도를 표시·변경하지 않음 (실시간 값과 불일치 방지) -->
        <div
          class="device-item-row"
          :class="{ 'device-item-row-disabled': !powerValue }"
          v-if="showLampSnapshotSliders"
        >
          <Slider
            name="밝기"
            :id="`color-bright-${props.device._id}`"
            thin
            filled-track
            track-variant="bright"
            :min="10"
            :max="100"
            :step="10"
            :disabled="!powerValue"
            :default="brightValue"
            value-suffix="%"
            @onSliderChanged="onBrightChanged"
          />
        </div>
        <div
          class="device-item-row"
          :class="{ 'device-item-row-disabled': !powerValue }"
          v-if="showLampSnapshotSliders"
        >
          <Slider
            name="색온도"
            :id="`color-temp-${props.device._id}`"
            thin
            filled-track
            track-variant="temp"
            :min="0"
            :max="20"
            :step="2"
            :disabled="!powerValue"
            :default="colorValue"
            v-on:onSliderChanged="onColorChanged"
          />
        </div>
        <div class="device-item-row" v-if="isCurtainType && isInclude">
          <Slider
            name="열림 정도"
            :id="`curtain-percent-${props.device._id}`"
            thin
            filled-track
            track-variant="neutral"
            :min="0"
            :max="100"
            :step="1"
            :default="percentValue"
            value-suffix="%"
            @onSliderChanged="onPercentChanged"
          />
        </div>
        <div class="device-item-row" v-if="isCurtainType && isInclude">
          <Checkbox
            name="커튼 방향"
            id="curtain-reverse"
            :checked="reverseValue"
            :disabled="true"
            :value="props.device._id"
            :valueText="reverseText"
            :isVisible="false"
            @onCheckboxChanged="onReverseChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onMounted,
  watch,
} from 'vue';
import Card from '@/common/template/components/bootstrap/Card.vue';
import CardBody from '@/common/template/components/bootstrap/CardBody.vue';
import Axios, { DeviceApi } from '@/api';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { MQTTCurtainCommand, MQTTDooyaRonoCommand, MQTTDooyaRonoAction } from '@/common/constants';
import Slider from '@/common/components/widgets/Slider.vue';
import Checkbox from '@/common/components/widgets/Checkbox.vue';
import OnOffSwitch from '@/common/components/widgets/OnOffSwitch.vue';
import useMqttControl from '@/common/hooks/useMqttControl';

const mqttControl = reactive(useMqttControl());

const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const props = defineProps({
  group: { type: Object, default: null },
  device: { type: Object, default: null },
  deviceType: { type: [String, Number], default: null },
  selectedDevices: { type: Array, default: () => [] },
  test: { type: [String, Number, Boolean, Object], default: null },
  // Boolean 선언 필수: attribute `selectable` 단독 사용 시 ""(falsy) 방지
  selectable: { type: Boolean, default: false },
  /** 가상버튼 타방 조명: 포함=멤버, 미포함=제외 (+포함 시 스냅샷 편집) */
  optionalInclude: { type: Boolean, default: false },
  /** 흡수 호스트 등: 포함 고정(토글 불가) */
  includeLocked: { type: Boolean, default: false },
});
const store = useStore();

const emit = defineEmits(['onDeviceChecked', 'deviceChecked', 'snapshotChanged', 'showStateChanged']);

const isInclude = ref(false);
const showStateOn = ref(false);
const includeLocked = computed(() => !!props.includeLocked || !!props.device?._absorbFixed);
/** 흡수 호스트(삭제된 Device): 슬라이더 값을 로컬·selected 스냅샷으로 유지 */
const absorbBright = ref(null);
const absorbColor = ref(null);

const isLampType = computed(() => Number(props.deviceType) === 1);
const isCurtainType = computed(() => {
  const t = Number(props.deviceType);
  return t === 3 || t === 5 || t === 6;
});

/** 조명 버튼 매핑 선택 모드 */
const isMappingSelectMode = computed(
  () => !!props.selectable && isLampType.value,
);

/** 가상버튼 타방 조명 포함 선택 모드 */
const isOptionalLampInclude = computed(
  () => !!props.optionalInclude && isLampType.value,
);

const showLampSnapshotSliders = computed(() => {
  if (!isLampType.value) return false;
  if (isMappingSelectMode.value) return false;
  // 흡수 고정 호스트도 씬 밝기/색온도 편집 가능
  if (includeLocked.value && isOptionalLampInclude.value) return true;
  if (isOptionalLampInclude.value) return isInclude.value;
  return true;
});

const liveDevice = computed(() => {
  const zones = store.state.home?.zones || [];
  for (const zone of zones) {
    for (const g of zone.groups || []) {
      const found = (g.devices || []).find((d) => String(d._id) === String(props.device?._id));
      if (found) return found;
    }
  }
  return props.device;
});

const emitIncludeChanged = (checked) => {
  const payload = {
    device: props.device,
    group: props.group,
    isChecked: !!checked,
  };
  // Vue3: on* 이벤트명 이슈 회피 — 둘 다 발행
  emit('deviceChecked', payload);
  emit('onDeviceChecked', payload);
};

const onCurtainSelectToggled = () => {
  isInclude.value = !isInclude.value;
  emitIncludeChanged(isInclude.value);
};

const sameDeviceId = (a, b) => String(a) === String(b);

const selectedDevice = computed(() => {
  return props.selectedDevices?.find((d) => sameDeviceId(d._id, props.device._id));
});

const toSliderNumber = (raw, fallback = 0) => {
  if (Array.isArray(raw)) {
    if (raw.length === 0) {
      return fallback;
    }
    const n = Number(raw[0]);
    return Number.isNaN(n) ? fallback : n;
  }
  if (raw === undefined || raw === null || raw === '') {
    return fallback;
  }
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
};

watch(
  () => [props.device?._id, props.device?.item?.bright, props.device?.item?.color, includeLocked.value],
  () => {
    if (!includeLocked.value) return;
    absorbBright.value = toSliderNumber(
      selectedDevice.value?.item?.bright ?? props.device?.item?.bright,
      50,
    );
    absorbColor.value = toSliderNumber(
      selectedDevice.value?.item?.color ?? props.device?.item?.color,
      10,
    );
  },
  { immediate: true },
);

const brightValue = computed(() => {
  if (includeLocked.value && absorbBright.value != null) {
    return absorbBright.value;
  }
  if (isOptionalLampInclude.value && selectedDevice.value?.item?.bright != null) {
    return toSliderNumber(selectedDevice.value.item.bright, 50);
  }
  return toSliderNumber(liveDevice.value?.item?.state?.bright ?? liveDevice.value?.item?.bright, 10);
});
const colorValue = computed(() => {
  if (includeLocked.value && absorbColor.value != null) {
    return absorbColor.value;
  }
  if (isOptionalLampInclude.value && selectedDevice.value?.item?.color != null) {
    return toSliderNumber(selectedDevice.value.item.color, 0);
  }
  return toSliderNumber(liveDevice.value?.item?.state?.color ?? liveDevice.value?.item?.color, 0);
});
/** 실시간 점등 상태 (물리 조명) */
const livePowerOn = computed(() => {
  const item = liveDevice.value?.item ?? {};
  if (liveDevice.value?.onVirtual === true) {
    return (item.state?.onoff ?? 0) === 1;
  }
  return item.onoff === 1;
});

/**
 * 매핑/타방·커튼 선택: 버튼 상태 = 포함 여부 (실시간 점등과 무관)
 * 자기 방 조명 스냅샷: 실제 점등 상태
 */
const powerValue = computed(() => {
  if (isMappingSelectMode.value || isOptionalLampInclude.value) {
    return !!isInclude.value;
  }
  return livePowerOn.value;
});
const reverseValue = computed(() => {
  return (selectedDevice.value?.item?.reverse || props.device.item.reverse) == 1;
});
const reverseText = computed(() => {
  return (selectedDevice.value?.item?.reverse || props.device.item.reverse) == 1 ? '역방향' : '정방향'
});
const percentValue = computed(() => toSliderNumber(props.device.item?.percent, 0));

const lampControlState = computed(() => {
  if (includeLocked.value) {
    return {
      onoff: 1,
      bright: brightValue.value,
      color: colorValue.value,
    };
  }
  const item = liveDevice.value?.item ?? {};
  if (liveDevice.value?.onVirtual === true) {
    const st = item.state ?? {};
    return {
      onoff: toSliderNumber(st.onoff ?? 0, 0),
      bright: toSliderNumber(st.bright ?? item.bright, 50),
      color: toSliderNumber(st.color ?? item.color, 0),
    };
  }
  const toScalar = (raw, fallback) => {
    if (Array.isArray(raw)) return toSliderNumber(raw[0], fallback);
    return toSliderNumber(raw, fallback);
  };
  return {
    onoff: toScalar(item.onoff, 0),
    bright: toScalar(item.bright, 50),
    color: toScalar(item.color, 0),
  };
});

const emitAbsorbSnapshot = (bright, color) => {
  emit('snapshotChanged', {
    device: props.device,
    group: props.group,
    bright,
    color,
  });
};

const publishAbsorbPreview = (bright, color) => {
  const device = props.device || liveDevice.value;
  const portsRaw = device?.item?.ports;
  const ports = Array.isArray(portsRaw)
    ? portsRaw.map((p) => Number(p))
    : portsRaw != null
      ? [Number(portsRaw)]
      : [];
  if (!device?.controllerId || ports.length === 0) return;
  mqttControl.onPublish(device.controllerId, device._id, {
    serial: device.controllerId,
    cmd: 'cs',
    type: 1,
    data: {
      ports,
      onoff: ports.map(() => 1),
      bright: ports.map(() => bright),
      color: ports.map(() => color),
    },
  });
};

const onPowerClicked = (value) => {
  if (includeLocked.value) {
    return;
  }
  const device = liveDevice.value;
  // 매핑 모드에서는 powerValue=포함 여부이므로, 토글 목표를 포함 기준으로 계산
  const currentlyOn = (isMappingSelectMode.value || isOptionalLampInclude.value)
    ? !!isInclude.value
    : livePowerOn.value;
  const payload = { onoff: currentlyOn ? 0 : 1 };
  const st = lampControlState.value;

  // 매핑·타방: 포함↔미포함 동기화. 미포함으로 바꾸면 상태 표시도 끈다.
  if (isMappingSelectMode.value || isOptionalLampInclude.value) {
    isInclude.value = payload.onoff === 1;
    if (!isInclude.value) {
      showStateOn.value = false;
      emit('showStateChanged', { device: props.device, showState: false });
    }
    emitIncludeChanged(isInclude.value);
  }

  if (device.type == 1) {
    const ports = device.item.ports.map((p) => p);
    /*
     * 매핑 선택 미리보기: 선택한 포트만 점등해야 함.
     * cmd c 는 보드 btn_link / zone cm 으로 하위(교차) 매핑까지 확장하므로 cs 사용.
     */
    if (isMappingSelectMode.value) {
      mqttControl.onPublish(device.controllerId, device._id, {
        serial: device.controllerId,
        cmd: 'cs',
        type: 1,
        data: {
          ports,
          onoff: ports.map(() => payload.onoff),
          bright: ports.map(() => st.bright),
          color: ports.map(() => st.color),
        },
      });
      return;
    }
    var data = {
      ports,
      onoff: payload.onoff,
      bright: st.bright,
      color: st.color,
    };
  } else {
    var data = { serial: device.item.serial, ...payload };
  }
  mqttControl.onPublish(device.controllerId, device._id, {
    serial: device.controllerId,
    cmd: 'c',
    type: device.type,
    data: data,
  });
}

const onBrightChanged = (e) => {
  const bright = parseInt(e, 10);
  if (includeLocked.value) {
    absorbBright.value = bright;
    const color = colorValue.value;
    emitAbsorbSnapshot(bright, color);
    publishAbsorbPreview(bright, color);
    return;
  }
  const device = liveDevice.value;
  const st = lampControlState.value;
  const onoff = ((isMappingSelectMode.value || isOptionalLampInclude.value) && isInclude.value)
    ? 1
    : st.onoff;
  mqttControl.onPublish(device.controllerId, device._id, {
    serial: device.controllerId,
    cmd: 'c',
    type: 1,
    data: {
      ports: device.item.ports.map((p) => p),
      onoff,
      bright,
      color: st.color,
    },
  });
};

const onColorChanged = (e) => {
  const color = parseInt(e, 10);
  if (includeLocked.value) {
    absorbColor.value = color;
    const bright = brightValue.value;
    emitAbsorbSnapshot(bright, color);
    publishAbsorbPreview(bright, color);
    return;
  }
  const device = liveDevice.value;
  const st = lampControlState.value;
  const onoff = ((isMappingSelectMode.value || isOptionalLampInclude.value) && isInclude.value)
    ? 1
    : st.onoff;
  mqttControl.onPublish(device.controllerId, device._id, {
    serial: device.controllerId,
    cmd: 'c',
    type: 1,
    data: {
      ports: device.item.ports.map((p) => p),
      onoff,
      bright: st.bright,
      color,
    },
  });
};

const onPercentChanged = async (e) => {
  if (props.device.type == 3) {
    mqttControl.onPublishTemp(props.device.controllerId, MQTTCurtainCommand.IsSetClosePercent, `${props.device.item.address},${e}`);
  } else if (props.device.type == 5) {
    mqttControl.onPublish(props.device.controllerId, props.device._id, {
      cmd: MQTTDooyaRonoCommand.DooyaCtrl,
      type: 1,
      data: {
        addr: parseInt(props.device.item.address, 10),
        action: MQTTDooyaRonoAction.Percent,
        percent: parseInt(e, 10),
      }
    });
  } else if (props.device.type == 6) {
    mqttControl.onPublish(props.device.controllerId, props.device._id, {
      cmd: MQTTDooyaRonoCommand.RonoCtrl,
      type: 1,
      data: {
        addr: parseInt(props.device.item.address, 10),
        action: MQTTDooyaRonoAction.Percent,
        percent: parseInt(e, 10),
      }
    });
  }
  await requestUpdateDevice(props.device._id, {
    item: { percent: e }
  });
};

const onReverseChanged = async (e) => {
  if (props.device.type == 3) {
    mqttControl.onPublishTemp(props.device.controllerId, MQTTCurtainCommand.IsWriteDirection, `${props.device.item.address},${e.checked ? '1' : '0'}`);
  } else if (props.device.type == 5) {
    mqttControl.onPublish(props.device.controllerId, props.device._id, {
      cmd: MQTTDooyaRonoCommand.DooyaCtrl,
      type: 1,
      data: {
        addr: parseInt(props.device.item.address, 10),
        action: MQTTDooyaRonoAction.Direction,
        value: e.checked ? 1 : 0,
      }
    });
  } else if (props.device.type == 6) {
    mqttControl.onPublish(props.device.controllerId, props.device._id, {
      cmd: MQTTDooyaRonoCommand.RonoCtrl,
      type: 1,
      data: {
        addr: parseInt(props.device.item.address, 10),
        action: MQTTDooyaRonoAction.Direction,
        value: e.checked ? 1 : 0,
      }
    });
  }
  await requestUpdateDevice(props.device._id, {
    item: { reverse: e.checked ? 1 : 0 }
  });
};

const requestUpdateDevice = async (id, params) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.patch(DeviceApi.UPDATE_DEVICE.replace(':id', id), params);
      if (result.resultCode === '0000') {
        store.commit('home/updateDevice', {
          groupId: props.group._id,
          id,
          data: params,
        });
        resolve(result);
      } else {
        emitter.emit('showPopAlert', {
          description: result.resultMessage,
        });
        reject(result.resultMessage);
      }
    } catch (error) {
      emitter.emit('showPopAlert', {
        description: '장치 정보 수정에 실패했습니다.',
      });
      reject('장치 정보 수정에 실패했습니다.');
    }
  });
};

const onShowStateClicked = (next) => {
  if (!isInclude.value) return;
  showStateOn.value = !!next;
  emit('showStateChanged', { device: props.device, showState: showStateOn.value });
};

watch(() => props.selectedDevices, (newSelectedDevices) => {
  const selected = newSelectedDevices?.find((d) => sameDeviceId(d._id, props.device._id));
  isInclude.value = !!selected;
  showStateOn.value = !!selected && selected.showState === true;
}, { immediate: true, deep: true });
</script>

<style scoped>
/* 심플 카드: 테두리 위주, 헤더 없음 */
.device-item-card {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem 0.5rem 1rem;
}

.device-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 0 1rem 0.4rem 1rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid #e9ecef;
  min-width: 0;
}

.device-item-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #212529;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-item-include-label {
  font-size: 0.8rem;
  margin-left: 0.25rem;
  color: #6c757d;
}

.include-locked-badge {
  pointer-events: none;
  cursor: default;
  white-space: nowrap;
}

.device-item-switches {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.device-item-switches :deep(button:disabled) {
  opacity: 0.45;
  cursor: default;
}

.device-item-body {
  padding-top: 0;
}

.device-item-row {
  padding: 0.25rem 0;
}

.device-item-row:not(:last-child) {
  border-bottom: 1px solid #f1f3f5;
}

.device-item-row-disabled {
  opacity: 0.65;
}
</style>
